import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { fetchMatchScoreboard } from '../../../services/matchService';
import { getCommunityId } from '../../../utils/community';
import { getClubId } from '../../../utils/club';
import PageShell from '../../../components/layout/PageShell';

const oversFromBalls = (balls = 0) => {
  const safeBalls = Number(balls) || 0;
  if (safeBalls <= 0) {
    return '0.0';
  }
  const overs = Math.floor((safeBalls - 1) / 6);
  const remaining = ((safeBalls - 1) % 6) + 1;
  return `${overs}.${remaining}`;
};

const normalizePowerPlays = (powerPlays = []) => {
  if (!Array.isArray(powerPlays)) return [];
  return powerPlays
    .map((entry) => ({
      startOver: Number(entry?.startOver),
      endOver: Number(entry?.endOver ?? entry?.startOver),
    }))
    .filter(
      (entry) =>
        Number.isFinite(entry.startOver) &&
        entry.startOver > 0 &&
        Number.isFinite(entry.endOver) &&
        entry.endOver > 0
    );
};

const formatPowerPlayLabel = (entry) =>
  entry.startOver === entry.endOver
    ? `Over ${entry.startOver}`
    : `Overs ${entry.startOver}-${entry.endOver}`;

const formatPowerPlaySummary = (powerPlays) =>
  (powerPlays || []).map(formatPowerPlayLabel).join(', ');

const extraTypeLabels = {
  wide: 'Wd',
  'no-ball': 'Nb',
  bye: 'B',
  'leg-bye': 'Lb',
  penalty: 'P',
  'jackpot-plus': 'JP',
  'jackpot-minus': 'JM',
};

const wicketTypeLabels = new Map([
  ['bowled', 'Bowled'],
  ['caught', 'Caught'],
  ['lbw', 'LBW'],
  ['run-out', 'Run out'],
  ['stumped', 'Stumped'],
  ['hit-wicket', 'Hit wicket'],
  ['retired-hurt', 'Retired hurt'],
  ['retired-out', 'Retired out'],
  ['obstructing-the-field', 'Obstructing the field'],
  ['hit-the-ball-twice', 'Hit the ball twice'],
  ['handled-ball', 'Handled the ball'],
  ['timed-out', 'Timed out'],
]);
const formatWicketTypeLabel = (wicketType = '') =>
  wicketTypeLabels.get(wicketType) || (wicketType ? wicketType.replace(/-/g, ' ') : '');
const getRetirementTypeFromEvent = (event) => {
  const wicketType = typeof event?.wicketType === 'string' ? event.wicketType : '';
  return wicketType.startsWith('retired') ? wicketType : '';
};
const isRetiredHurt = (event) => Boolean(event?.isWicket && getRetirementTypeFromEvent(event) === 'retired-hurt');
const isActualWicket = (event) => Boolean(event?.isWicket && !getRetirementTypeFromEvent(event));

const getRetirementType = (batter) => {
  const dismissalType = typeof batter?.dismissalType === 'string' ? batter.dismissalType : '';
  if (dismissalType.startsWith('retired')) return dismissalType;
  const retirementType = typeof batter?.retirementType === 'string' ? batter.retirementType : '';
  if (retirementType.startsWith('retired')) return retirementType;
  const wicketType = typeof batter?.wicketType === 'string' ? batter.wicketType : '';
  if (batter?.isRetired || batter?.isRetiredHurt || wicketType.startsWith('retired')) return wicketType || 'retired-hurt';
  return '';
};

const wicketTypesWithoutFieldingDetails = new Set([
  'bowled',
  'lbw',
  'retired-out',
  'hit-the-ball-twice',
  'handled-ball',
  'timed-out',
  'obstructing-the-field',
  'hit-wicket',
]);
const shouldShowWicketFieldingDetails = (event) =>
  !isRetiredHurt(event) && !wicketTypesWithoutFieldingDetails.has(event?.wicketType);

const extractRunsFromResult = (result) => {
  const match = String(result ?? '').match(/-?\d+/);
  if (!match) return null;
  const value = Number(match[0]);
  return Number.isFinite(value) ? value : null;
};

const resolveExtraDisplayRuns = (event) => {
  const rawRuns = Number(event?.runs);
  const resultRuns = extractRunsFromResult(event?.result);
  const totalRuns =
    Number.isFinite(rawRuns) && (rawRuns !== 0 || !Number.isFinite(resultRuns) || resultRuns === 0)
      ? rawRuns
      : resultRuns ?? 0;
  const extraType = event?.extraType || '';
  if (extraType === 'jackpot-minus') {
    return Math.abs(totalRuns);
  }
  if (extraType === 'no-ball') {
    const recordedBatRuns = Number(event?.batRuns);
    if (Number.isFinite(recordedBatRuns)) {
      return Math.max(0, recordedBatRuns);
    }
    return Math.max(0, totalRuns - 1);
  }
  if (extraType === 'wide') {
    return Math.max(0, totalRuns - 1);
  }
  return Math.max(0, totalRuns);
};

const formatBallDisplay = (event) => {
  if (!event) return '';
  if (isActualWicket(event)) return 'W';
  if (isRetiredHurt(event)) return 'RH';
  if (event.extraType) {
    const runs = resolveExtraDisplayRuns(event);
    const extraLabel = extraTypeLabels[event.extraType] || event.extraType;
    return runs > 0 ? `${runs}${extraLabel}` : extraLabel;
  }
  return `${event.runs ?? 0}`;
};

const formatWicketSummary = (event, resolvePlayerName) => {
  if (!event?.isWicket) return 'No';
  const parts = [];
  if (event.wicketType) parts.push(formatWicketTypeLabel(event.wicketType));
  if (event.wicketLocation && shouldShowWicketFieldingDetails(event)) {
    parts.push(event.wicketLocation);
  }
  if (event.wicketFielder && shouldShowWicketFieldingDetails(event)) {
    parts.push(`fielder: ${resolvePlayerName(event.wicketFielder) || event.wicketFielder}`);
  }
  return parts.length > 0 ? parts.join(' • ') : 'Yes';
};

const formatBallDetails = (event, resolvePlayerName) => {
  if (!event) return '';
  const details = [];
  if (event.shotType) details.push(`Shot: ${event.shotType}`);
  if (event.shotPlacement) details.push(`Placement: ${event.shotPlacement}`);
  if (event.isWicket) {
    const wicketDetails = formatWicketSummary(event, resolvePlayerName);
    if (wicketDetails !== 'No') {
      details.push(`Wicket: ${wicketDetails}`);
    }
  }
  return details.join(' | ');
};

const buildCurrentOverFromEvents = (events = [], ballsInOver = 0) => {
  if (events.length === 0) return null;

  const totalBallsInOver = Number(ballsInOver);
  if (!Number.isFinite(totalBallsInOver)) return null;

  let countedBalls = 0;
  const currentOverEvents = [];

  for (let index = events.length - 1; index >= 0; index -= 1) {
    const event = events[index];
    if (event?.countBall && countedBalls === totalBallsInOver) {
      break;
    }
    currentOverEvents.unshift(event);
    if (event?.countBall) {
      countedBalls += 1;
    }
  }

  return {
    ballsInOver: totalBallsInOver,
    remaining: 6 - totalBallsInOver,
    events: currentOverEvents,
  };
};

const buildBallLabels = (events = []) => {
  let legalBallCount = 0;
  return events.map((event) => {
    if (event?.countBall) {
      legalBallCount += 1;
      const overNumber = Math.floor((legalBallCount - 1) / 6) + 1;
      const ballInOver = ((legalBallCount - 1) % 6) + 1;
      return `${overNumber}.${ballInOver}`;
    }
    const overNumber = Math.floor(legalBallCount / 6) + 1;
    const ballInOver = legalBallCount % 6;
    return `${overNumber}.${ballInOver}`;
  });
};

const normalizeName = (value) => (typeof value === 'string' ? value.trim().toLowerCase() : '');

const addScorerCandidate = (candidates, value) => {
  if (value === undefined || value === null || value === '') return;
  if (Array.isArray(value)) {
    value.forEach((entry) => addScorerCandidate(candidates, entry));
    return;
  }
  candidates.push(value);
};

const collectScorerCandidates = (...sources) => {
  const candidates = [];
  sources.filter(Boolean).forEach((source) => {
    addScorerCandidate(candidates, source.scorer);
    addScorerCandidate(candidates, source.scorerId);
    addScorerCandidate(candidates, source.assignedScorer);
    addScorerCandidate(candidates, source.assignedScorerId);
    addScorerCandidate(candidates, source.assignedScorers);
  });
  return candidates;
};

const scorerCandidateMatchesAliases = (candidate, aliases) => {
  if (!candidate || !aliases?.size) return false;
  if (typeof candidate === 'string' || typeof candidate === 'number') {
    return aliases.has(normalizeName(String(candidate)));
  }
  if (typeof candidate !== 'object') return false;
  return [
    candidate._id,
    candidate.id,
    candidate.userId,
    candidate.user,
    candidate.playerId,
    candidate.player,
    candidate.scorerId,
    candidate.assignedScorerId,
    candidate.value,
    candidate.name,
    candidate.fullName,
    candidate.playerName,
    candidate.scorerName,
    candidate.email,
    candidate.mobile,
    candidate.phone,
    candidate.label,
    candidate.user?.name,
    candidate.user?.email,
    candidate.user?.mobile,
    candidate.player?.name,
    candidate.player?.fullName,
    candidate.player?.email,
    candidate.player?.mobile,
    candidate.personal?.fullName,
    candidate.personal?.email,
    candidate.personal?.mobile,
  ].some((value) => aliases.has(normalizeName(String(value || ''))));
};

// ✅ Custom breadcrumbs example:
// const crumbs = [
//   { label: 'Home', to: '/' },
//   { label: 'Community', to: `/community/${communityId}` },
//   { label: 'Match Manager', to: `/community/${communityId}/match-manager/${managerId}` },
//   { label: 'Manual Match Format' },
// ];
// <PageShell breadcrumbs={crumbs}>...</PageShell>
// <PageShell breadcrumbOverrides={{ tournamentId: tournament?.name || 'Tournament' }}>
const MatchScoreboardPage = () => {
  const {
    organizationId,
    communityId: routeCommunityId,
    clubId: routeClubId,
    individualTournamentId,
    tournamentId,
    managerId,
    matchId,
  } = useParams();
  const { token, user } = useAuth();
  const communityId = useMemo(() => getCommunityId(routeCommunityId), [routeCommunityId]);
  const clubId = useMemo(() => getClubId(routeClubId), [routeClubId]);
  const entityType = individualTournamentId
    ? 'individual'
    : routeCommunityId
      ? 'community'
      : routeClubId
        ? 'club'
        : organizationId
          ? 'organization'
          : '';
  const [match, setMatch] = useState(null);
  const [tournament, setTournament] = useState(null);
  const [players, setPlayers] = useState([]);
  const [scoreboardSummary, setScoreboardSummary] = useState(null);
  const [inningsSummaries, setInningsSummaries] = useState([]);
  const [matchAwards, setMatchAwards] = useState(null);
  const [matchComplete, setMatchComplete] = useState(false);
  const [matchResult, setMatchResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const userRoles = useMemo(
    () => new Set([...(user?.roles || []), user?.role].filter(Boolean)),
    [user]
  );
  const userTeamRoles = useMemo(() => new Set([...(user?.teamRoles || [])].filter(Boolean)), [user]);
  const isIndividualAccount = userRoles.has('individual') || user?.accountType === 'individual';
  const isScorerRole = userRoles.has('scorer') || userTeamRoles.has('scorer') || isIndividualAccount;
  const scorerAliases = useMemo(() => {
    const aliases = new Set();
    [
      user?._id,
      user?.id,
      user?.name,
      user?.fullName,
      user?.email,
      user?.mobile,
      user?.phone,
      user?.playerProfile?._id,
      user?.playerProfile?.id,
      user?.playerProfile?.fullName,
      user?.playerProfile?.email,
      user?.playerProfile?.mobile,
      user?.playerProfile?.phone,
    ].forEach((value) => {
      const normalized = normalizeName(value);
      if (normalized) {
        aliases.add(normalized);
      }
    });
    return aliases;
  }, [user]);

  useEffect(() => {
    const loadScoreboard = async () => {
      if (!token || !tournamentId || !matchId) return;
      setIsLoading(true);
      setError('');

      try {
        console.log('SCOREBOARD_ROUTE_PARAMS', {
          tournamentId,
          matchId,
          managerId,
          entityType,
        });
        console.log('MATCH_ID_FROM_ROUTE', matchId);
        console.log('TOURNAMENT_ID_FROM_ROUTE', tournamentId);

        const response = await fetchMatchScoreboard({ token, tournamentId, matchId });
        console.log('MATCH_RESPONSE', response.match);
        console.log('TOURNAMENT_RESPONSE', response.tournament);
        setMatch(response.match || null);
        setTournament(response.tournament || null);
        setPlayers(response.players || []);
        setScoreboardSummary(response.scoreboardSummary || null);
        setInningsSummaries(response.inningsSummaries || []);
        setMatchAwards(response.matchAwards || null);
        setMatchComplete(Boolean(response.matchComplete));
        setMatchResult(response.matchResult || null);
      } catch (err) {
        setMatch(null);
        setTournament(null);
        setPlayers([]);
        setScoreboardSummary(null);
        setInningsSummaries([]);
        setMatchAwards(null);
        setMatchComplete(false);
        setMatchResult(null);
        setError(err.message || 'Unable to load scoreboard.');
      } finally {
        setIsLoading(false);
      }
    };

    loadScoreboard();
  }, [entityType, managerId, matchId, token, tournamentId]);

  const playerDirectory = useMemo(() => {
    const entries = (players || []).map((player) => [player.id, player.name]);
    return new Map(entries.filter((entry) => entry[0]));
  }, [players]);

  const resolvePlayerName = (playerId) => {
    if (!playerId) return '';
    return playerDirectory.get(playerId) || playerId;
  };

  const ballDetails = (event) => formatBallDetails(event, resolvePlayerName);
  const formatWicketDetails = (event) => formatWicketSummary(event, resolvePlayerName);

  const score = match?.score || null;
  const scoreExtras = scoreboardSummary?.extras || {};
  const events = score?.events || [];
  const ballLabels = useMemo(() => buildBallLabels(events), [events]);
  const ballsInOver = (Number(score?.balls) || 0) % 6;
  const derivedCurrentOver = useMemo(
    () => buildCurrentOverFromEvents(events, ballsInOver),
    [ballsInOver, events]
  );
  const runRate = scoreboardSummary?.runRate ?? 0;
  const tossWinner = match?.configuration?.tossWinner || '';
  const tossDecision = match?.configuration?.tossDecision || '';
  const tossSummary =
    tossWinner && tossDecision
      ? `${tossWinner} won the toss and chose to ${tossDecision === 'bat' ? 'bat' : 'field'}.`
      : '';
  const displayBattingTeam = score?.battingTeam || match?.configuration?.battingTeam || match?.team1 || '';
  const displayBowlingTeam = score?.bowlingTeam || match?.configuration?.bowlingTeam || match?.team2 || '';
  const configuredPowerPlayStart = Number(match?.configuration?.powerPlayStartOver);
  const configuredPowerPlayEnd = Number(match?.configuration?.powerPlayEndOver);
  const fallbackPowerPlay = useMemo(
    () =>
      Number.isFinite(configuredPowerPlayStart) && configuredPowerPlayStart > 0
        ? [
            {
              startOver: configuredPowerPlayStart,
              endOver:
                Number.isFinite(configuredPowerPlayEnd) && configuredPowerPlayEnd > 0
                  ? configuredPowerPlayEnd
                  : configuredPowerPlayStart,
            },
          ]
        : [],
    [configuredPowerPlayEnd, configuredPowerPlayStart]
  );
  const powerPlays = useMemo(() => {
    const saved = normalizePowerPlays(score?.powerPlays);
    return saved.length ? saved : fallbackPowerPlay;
  }, [fallbackPowerPlay, score?.powerPlays]);
  const totalBalls = Number(score?.balls) || 0;
  const safeBalls = Number(totalBalls) || 0;

  const currentOverNumber =
    safeBalls === 0
      ? 1
      : Math.floor((safeBalls - 1) / 6) + 1;

  const hasPowerPlay = Array.isArray(powerPlays) && powerPlays.length > 0;
  const isPowerPlayOver =
    hasPowerPlay &&
    powerPlays.some(
      (entry) =>
        currentOverNumber >= entry.startOver && currentOverNumber <= entry.endOver
    );
  const isMatchFinished =
    matchComplete ||
    (Number(score?.currentInnings) >= 2 &&
      (score?.status === 'completed' || score?.status === 'all_out'));
  const defaultOver = { events: [], ballsInOver, remaining: 6 - ballsInOver };
  const currentOver =
    score?.currentOver?.events?.length > 0
      ? score.currentOver
      : derivedCurrentOver || score?.currentOver || defaultOver;
  const currentOverEvents = currentOver.events || [];
  const displayOver = currentOver;
  const displayOverEvents = displayOver?.events || [];
  const isAssignedScorer = useMemo(() => {
    if (!isScorerRole || !scorerAliases.size) {
      return false;
    }
    return collectScorerCandidates(match, match?.configuration).some((candidate) =>
      scorerCandidateMatchesAliases(candidate, scorerAliases)
    );
  }, [isScorerRole, match, scorerAliases]);
  const currentOverRows = useMemo(() => {
    let ballNumber = 0;
    return displayOverEvents.map((event) => {
      let label = '•';
      if (event?.countBall) {
        ballNumber += 1;
        label = `${ballNumber}`;
      }
      return { label, event };
    });
  }, [displayOverEvents]);

  const inningsStats = useMemo(() => {
    if (Array.isArray(inningsSummaries) && inningsSummaries.length > 0) {
      return inningsSummaries;
    }
    if (scoreboardSummary) {
      return [
        {
          number: Number(score?.currentInnings) || 1,
          battingTeam: score?.battingTeam,
          bowlingTeam: score?.bowlingTeam,
          runs: score?.runs || 0,
          wickets: score?.wickets || 0,
          balls: score?.balls || 0,
          extras: scoreboardSummary?.extras || {},
          status: score?.status,
          summary: scoreboardSummary,
        },
      ];
    }
    return [];
  }, [inningsSummaries, score, scoreboardSummary]);

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="py-4">
      <div className="mb-4 d-flex flex-column gap-2">
        <p className="eyebrow text-uppercase text-primary fw-semibold mb-0">Match scoreboard</p>
        <h1 className="h3 mb-1">Scoreboard</h1>
        <p className="text-muted mb-0">
          {tournament?.name || 'Tournament'} • {match?.team1 || 'Team 1'} vs {match?.team2 || 'Team 2'}
        </p>
        {match && (
          <div className="d-flex align-items-center gap-3">
            <div className="d-flex align-items-center gap-2">
              {match.team1Logo ? <img src={match.team1Logo} alt={match.team1 || 'Team 1'} className="rounded border" style={{ width: 28, height: 28, objectFit: 'cover' }} /> : null}
              <span className="small">{match.team1 || 'Team 1'}</span>
            </div>
            <span className="text-muted small">vs</span>
            <div className="d-flex align-items-center gap-2">
              {match.team2Logo ? <img src={match.team2Logo} alt={match.team2 || 'Team 2'} className="rounded border" style={{ width: 28, height: 28, objectFit: 'cover' }} /> : null}
              <span className="small">{match.team2 || 'Team 2'}</span>
            </div>
          </div>
        )}
        {(communityId || organizationId || clubId || individualTournamentId) && managerId && tournamentId && (
          <div className="d-flex gap-2 flex-wrap">
            <Link
              className="btn btn-sm btn-outline-secondary"
              to={
                communityId
                  ? `/community/${communityId}/match-manager/${managerId}/tournaments/${tournamentId}`
                  : clubId
                    ? `/club/${clubId}/match-manager/${managerId}/tournaments/${tournamentId}`
                    : individualTournamentId
                      ? `/individual-tournament/${individualTournamentId}/match-manager/${managerId}/tournaments/${tournamentId}`
                      : `/org/${organizationId}/match-manager/${managerId}/tournaments/${tournamentId}`
              }
            >
              Back to match schedule
            </Link>
            {matchId && isAssignedScorer && (
              <Link
                className="btn btn-sm btn-outline-primary"
                to={
                  communityId
                    ? `/community/${communityId}/match-manager/${managerId}/tournaments/${tournamentId}/matches/${matchId}/start`
                    : clubId
                      ? `/club/${clubId}/match-manager/${managerId}/tournaments/${tournamentId}/matches/${matchId}/start`
                      : individualTournamentId
                        ? `/individual-tournament/${individualTournamentId}/match-manager/${managerId}/tournaments/${tournamentId}/matches/${matchId}/start`
                        : `/org/${organizationId}/match-manager/${managerId}/tournaments/${tournamentId}/matches/${matchId}/start`
                }
              >
                Open scoring desk
              </Link>
            )}
          </div>
        )}
      </div>

      {(error || (!match && !isLoading)) && (
        <div className="alert alert-danger" role="alert">
          {error || 'Scoreboard details are unavailable.'}
        </div>
      )}

      {isLoading && (
        <div className="alert alert-secondary" role="alert">
          Loading scoreboard...
        </div>
      )}

      {match && (
        <div className="row g-4">
          <div className="col-12 col-lg-4">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body d-flex flex-column gap-3">
                <div>
                  <p className="text-uppercase text-muted small mb-1">Score</p>
                  <h2 className="h2 mb-0">
                    {score?.runs || 0}-{score?.wickets || 0}
                  </h2>
                  <p className="text-muted mb-0">
                    Overs: {oversFromBalls(score?.balls)}
                    {score?.overs ? ` / ${score?.overs}` : ''}
                  </p>
                  <p className="text-muted mb-0">Run rate: {runRate}</p>
                  {hasPowerPlay ? (
                    <p className="text-muted mb-0">
                      Power plays: {formatPowerPlaySummary(powerPlays)}
                      {isPowerPlayOver ? (
                        <span className="badge bg-warning-subtle text-warning-emphasis ms-2">
                          Live
                        </span>
                      ) : null}
                    </p>
                  ) : null}
                </div>
                <div>
                  <p className="text-uppercase text-muted small mb-1">Batting</p>
                  <p className="mb-0 fw-semibold">{displayBattingTeam}</p>
                  <p className="text-muted mb-0">Bowling: {displayBowlingTeam}</p>
                </div>
                {tossSummary ? (
                  <div>
                    <p className="text-uppercase text-muted small mb-1">Toss</p>
                    <p className="text-muted mb-0">{tossSummary}</p>
                  </div>
                ) : null}
                <div>
                  <p className="text-uppercase text-muted small mb-1">Players</p>
                  <p className="mb-0 text-muted">
                    Striker: {resolvePlayerName(score?.striker) || 'Not set'}
                  </p>
                  <p className="mb-0 text-muted">
                    Non-striker: {resolvePlayerName(score?.nonStriker) || 'Not set'}
                  </p>
                  <p className="mb-0 text-muted">
                    Bowler: {resolvePlayerName(score?.bowler) || 'Not set'}
                    {score?.bowlerSide ? ` (${score.bowlerSide} side)` : ''}
                  </p>
                </div>
                <div>
                  <p className="text-uppercase text-muted small mb-1">Extras</p>
                  <div className="text-muted small">
                    Wides {scoreExtras.wides || 0} • No balls {scoreExtras.noBalls || 0} • Byes {scoreExtras.byes || 0}
                  </div>
                  <div className="text-muted small">
                    Leg byes {scoreExtras.legByes || 0} • Penalty {scoreExtras.penalty || 0}
                  </div>
                </div>
                <div>
                  <p className="text-uppercase text-muted small mb-1">Status</p>
                  <span className="badge bg-primary-subtle text-primary-emphasis">
                    {isMatchFinished
                      ? 'Completed'
                      : score?.status === 'all_out'
                        ? 'All out'
                        : score?.status === 'completed'
                          ? 'Completed'
                          : score?.status === 'in_progress'
                            ? 'Live'
                            : 'Not started'}
                  </span>
                </div>
                {matchResult?.text ? (
                  <div>
                    <p className="text-uppercase text-muted small mb-1">Result</p>
                    <p className="text-muted mb-0">{matchResult.text}</p>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          <div className="col-12 col-lg-8">
            <div className="d-flex flex-column gap-4">
              <div className="card shadow-sm border-0">
                <div className="card-body d-flex flex-column gap-3">
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <h2 className="h5 mb-0">Match awards</h2>
                    <span className="text-muted small">Best performer highlights</span>
                  </div>
                  {isMatchFinished && matchAwards ? (
                    <div className="row g-3">
                      <div className="col-12 col-md-6">
                        <div className="p-3 border rounded h-100">
                          <p className="text-uppercase text-muted small mb-1">Man of the match</p>
                          <p className="mb-1 fw-semibold">
                            {matchAwards.manOfTheMatch?.name || matchAwards.manOfTheMatch?.playerId || 'TBD'}
                          </p>
                          <p className="text-muted small mb-0">
                            Runs: {matchAwards.manOfTheMatch?.runs || 0} • Wickets:{' '}
                            {matchAwards.manOfTheMatch?.wickets || 0}
                          </p>
                        </div>
                      </div>
                      <div className="col-12 col-md-6">
                        <div className="p-3 border rounded h-100">
                          <p className="text-uppercase text-muted small mb-1">Best batsman</p>
                          <p className="mb-1 fw-semibold">
                            {matchAwards.bestBatsman?.name || matchAwards.bestBatsman?.playerId || 'TBD'}
                          </p>
                          <p className="text-muted small mb-0">
                            Runs: {matchAwards.bestBatsman?.runs || 0} • Balls:{' '}
                            {matchAwards.bestBatsman?.balls || 0}
                          </p>
                        </div>
                      </div>
                      <div className="col-12 col-md-6">
                        <div className="p-3 border rounded h-100">
                          <p className="text-uppercase text-muted small mb-1">Best bowler</p>
                          <p className="mb-1 fw-semibold">
                            {matchAwards.bestBowler?.name || matchAwards.bestBowler?.playerId || 'TBD'}
                          </p>
                          <p className="text-muted small mb-0">
                            Wickets: {matchAwards.bestBowler?.wickets || 0} • Overs:{' '}
                            {matchAwards.bestBowler?.overs || 0}
                          </p>
                        </div>
                      </div>
                      <div className="col-12 col-md-6">
                        <div className="p-3 border rounded h-100">
                          <p className="text-uppercase text-muted small mb-1">Best fielder</p>
                          <p className="mb-1 fw-semibold">
                            {matchAwards.bestFielder?.name || matchAwards.bestFielder?.playerId || 'TBD'}
                          </p>
                          <p className="text-muted small mb-0">
                            Dismissals: {matchAwards.bestFielder?.dismissals || 0}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="alert alert-light mb-0">
                      Match awards will appear after the second innings is complete.
                    </div>
                  )}
                </div>
              </div>

              <div className="card shadow-sm border-0">
                <div className="card-body d-flex flex-column gap-3">
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <h2 className="h5 mb-0">Innings scorecards</h2>
                    <span className="text-muted small">Batting, bowling, and fall of wickets</span>
                  </div>
                  {inningsStats.length === 0 ? (
                    <div className="alert alert-light mb-0">No innings data yet.</div>
                  ) : (
                    <div className="d-flex flex-column gap-4">
                      {inningsStats.map((innings) => {
                        const summary = innings.summary || {};
                        const battingSummary = summary.batting || [];
                        const bowlingSummary = summary.bowling || [];
                        const fallOfWickets = summary.fallOfWickets || [];
                        const scoreline = `${innings.runs || 0}-${innings.wickets || 0}`;
                        const oversLabel = oversFromBalls(innings.balls);
                        const headerLabel = `Innings ${innings.number}${
                          innings.battingTeam ? ` • ${innings.battingTeam}` : ''
                        }`;

                        return (
    <PageShell containerClassName="p-0">
                          <div key={`innings-${innings.number}`} className="border rounded-3 p-3">
                            <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2 mb-3">
                              <div>
                                <p className="fw-semibold mb-1">{headerLabel}</p>
                                <p className="text-muted small mb-0">
                                  {innings.bowlingTeam ? `vs ${innings.bowlingTeam}` : 'Bowling team TBD'}
                                </p>
                              </div>
                              <div className="text-md-end">
                                <p className="fw-semibold mb-1">
                                  {scoreline} ({oversLabel} ov)
                                </p>
                                <p className="text-muted small mb-0">Run rate: {summary.runRate ?? 0}</p>
                              </div>
                            </div>

                            {battingSummary.length === 0 ? (
                              <div className="alert alert-light mb-3">No batting data yet.</div>
                            ) : (
                              <div className="table-responsive mb-3">
                                <table className="table table-striped align-middle">
                                  <thead>
                                    <tr>
                                      <th scope="col">Batter</th>
                                      <th scope="col">R</th>
                                      <th scope="col">B</th>
                                      <th scope="col">4s</th>
                                      <th scope="col">6s</th>
                                      <th scope="col">SR</th>
                                      <th scope="col">Status</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {battingSummary.map((batter, index) => (
                                      <tr key={batter.playerId || `batter-${index}`}>
                                        <td>{resolvePlayerName(batter.playerId) || '-'}</td>
                                        <td>{batter.runs}</td>
                                        <td>{batter.balls}</td>
                                        <td>{batter.fours}</td>
                                        <td>{batter.sixes}</td>
                                        <td>{batter.strikeRate ?? '-'}</td>
                                        <td>
                                          {getRetirementType(batter)
                                            ? formatWicketTypeLabel(getRetirementType(batter))
                                            : batter.isOut
                                              ? 'Out'
                                              : 'Not out'}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}

                            {bowlingSummary.length === 0 ? (
                              <div className="alert alert-light mb-3">No bowling data yet.</div>
                            ) : (
                              <div className="table-responsive mb-3">
                                <table className="table table-striped align-middle">
                                  <thead>
                                    <tr>
                                      <th scope="col">Bowler</th>
                                      <th scope="col">O</th>
                                      <th scope="col">M</th>
                                      <th scope="col">R</th>
                                      <th scope="col">W</th>
                                      <th scope="col">Econ</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {bowlingSummary.map((bowler, index) => (
                                      <tr key={bowler.playerId || `bowler-${index}`}>
                                        <td>{resolvePlayerName(bowler.playerId) || '-'}</td>
                                        <td>{bowler.overs}</td>
                                        <td>{bowler.maidens}</td>
                                        <td>{bowler.runsConceded}</td>
                                        <td>{bowler.wickets}</td>
                                        <td>{bowler.economy ?? '-'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}

                            {fallOfWickets.length === 0 ? (
                              <div className="alert alert-light mb-0">No wickets yet.</div>
                            ) : (
                              <div className="table-responsive">
                                <table className="table table-striped align-middle">
                                  <thead>
                                    <tr>
                                      <th scope="col">Wicket</th>
                                      <th scope="col">Score</th>
                                      <th scope="col">Batter</th>
                                      <th scope="col">Overs</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {fallOfWickets.map((entry, index) => (
                                      <tr key={`${entry.wicket}-${index}`}>
                                        <td>{entry.wicket}</td>
                                        <td>
                                          {entry.runs}-{entry.wicket}
                                        </td>
                                        <td>{resolvePlayerName(entry.batterId) || '-'}</td>
                                        <td>{entry.overs}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        
    </PageShell>
  );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div className="card shadow-sm border-0">
                <div className="card-body d-flex flex-column gap-3">
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <h2 className="h5 mb-0">Current over</h2>
                    <span className="text-muted small">{displayOver.ballsInOver}/6 balls</span>
                  </div>
                  {currentOverRows.length === 0 ? (
                    <div className="alert alert-light mb-0">Over complete. Awaiting next delivery.</div>
                  ) : (
                    <div className="over-balls">
                      {currentOverRows.map(({ label, event }, index) => (
                        <span
                          key={`${event.timestamp || 'current-over'}-${index}`}
                          className={`over-ball${isActualWicket(event) ? ' over-ball--wicket' : ''}${
                            event.extraType ? ' over-ball--extra' : ''
                          }`}
                          title={`${label} ball${ballDetails(event) ? ` • ${ballDetails(event)}` : ''}`}
                        >
                          {formatBallDisplay(event)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="card shadow-sm border-0">
                <div className="card-body d-flex flex-column gap-3">
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <h2 className="h5 mb-0">Ball-by-ball log</h2>
                    <span className="text-muted small">{events.length} deliveries</span>
                  </div>
                  {events.length === 0 ? (
                    <div className="alert alert-light mb-0">No deliveries recorded yet.</div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-striped align-middle">
                        <thead>
                          <tr>
                            <th scope="col">Ball</th>
                            <th scope="col">Runs</th>
                            <th scope="col">Extra</th>
                            <th scope="col">Wicket</th>
                            <th scope="col">Shot type</th>
                            <th scope="col">Shot placement</th>
                            <th scope="col">Striker</th>
                            <th scope="col">Bowler</th>
                            <th scope="col">Side</th>
                          </tr>
                        </thead>
                        <tbody>
                          {events.map((event, index) => (
                            <tr key={`${event.timestamp || 'ball'}-${index}`}>
                              <td>{ballLabels[index]}</td>
                              <td>{event.runs}</td>
                              <td>{event.extraType || '-'}</td>
                              <td>{formatWicketDetails(event)}</td>
                              <td>{event.shotType || '-'}</td>
                              <td>{event.shotPlacement || '-'}</td>
                              <td>{resolvePlayerName(event.striker) || '-'}</td>
                              <td>{resolvePlayerName(event.bowler) || '-'}</td>
                              <td>{event.bowlerSide ? `${event.bowlerSide} side` : '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MatchScoreboardPage;
